'use client';
/**
 * LES STATISTIQUES DES NON-CONFORMITÉS — un onglet des NC (Ines, 20/10/2026 : « un module de génération de
 * statistiques de NC avec possibilité d'envoi par correspondant »). Il remplace « Analyse fine » et « Relevé aux
 * correspondants ».
 *
 *  · UNE PÉRIODE pour tout l'onglet : mois (le mois écoulé par défaut), trimestre, année ou dates libres.
 *  · UN PÉRIMÈTRE : tout le laboratoire, ou un correspondant.
 *  · Tout le laboratoire : chiffres clés comparés à la période précédente, évolution mois par mois, répartition, NC par
 *    correspondant, ce qui se répète. Calculé à l'écran sur le registre (NC RETENUES : ni au triage, ni annulées).
 *  · Un correspondant : ses statistiques et le détail de ses NC, calculés par le serveur — exactement ce que son
 *    document portera. Envoi UN PAR UN (arbitrage du 20/10/2026), par le CQ ou le RQ ; chaque envoi au registre.
 *
 * Adressage : `/statistiques-nc` (la tâche « Relevé mensuel à adresser » y mène), `?contact=ORG-…` ouvre un correspondant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import {
  NC_SOURCES, NC_GRAVITES, NC_TYPES, NC_STATES, NC_CANAUX, libelleCanal, natureMeta, natureDe, aujourdhuiIso,
} from '@/lib/meta';
import {
  depuis as ncDepuis, recurrences, periodeParDefaut, bornesStats, periodePrecedenteStats, moisEvolution, ncsEntre, moisEnClair,
  type PeriodeStats, type ModeStats,
} from '@/lib/stats-nc';
import { card, inputStyle, Selecteur, Pastille, EmptyState, enChargement, Skeleton } from '@/components/ui';
import { lbl } from './ressources/commun';
import { Segment } from '@/shared/Segment';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { useNomsContacts } from '@/components/ChoixCorrespondant';
import { PanneauReleve, Barre, ecartEnClair, type Ligne } from './annuaire/Releve';

type Dim = 'nature' | 'releve' | 'source' | 'canal' | 'gravite' | 'processus' | 'equipement' | 'correspondant' | 'statut';
const DIMS: { valeur: Dim; libelle: string }[] = [
  { valeur: 'nature', libelle: 'Nature' }, { valeur: 'releve', libelle: 'Relevée par' }, { valeur: 'canal', libelle: 'Canal de détection' },
  { valeur: 'source', libelle: 'Secteur (historique)' }, { valeur: 'gravite', libelle: 'Gravité' }, { valeur: 'equipement', libelle: 'Appareil' },
  { valeur: 'correspondant', libelle: 'Correspondant' }, { valeur: 'processus', libelle: 'Processus' }, { valeur: 'statut', libelle: 'Statut' },
];
const TRIMESTRES = ['T1', 'T2', 'T3', 'T4'];

const tuile = (valeur: React.ReactNode, libelle: string) => (
  <div style={{ ...card, padding: '10px 12px', display: 'grid', gap: 2 }}>
    <strong style={{ fontSize: 20, color: 'var(--text-strong)', fontFamily: 'var(--font-mono)' }}>{valeur}</strong>
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{libelle}</span>
  </div>
);
const titre = (t: string) => <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{t}</h3>;

/** La période de l'onglet : mois, trimestre, année ou dates libres. */
function BarrePeriode({ periode, onChange, aujourdhui }: { periode: PeriodeStats; onChange: (p: PeriodeStats) => void; aujourdhui: string }) {
  const an = Number(aujourdhui.slice(0, 4));
  const annees = Array.from({ length: 6 }, (_, i) => String(an - i));
  const maj = (p: Partial<PeriodeStats>) => onChange({ ...periode, ...p });
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div style={{ display: 'grid', gap: 5 }}>
        <span style={lbl}>Période</span>
        <Segment ariaLabel="Période des statistiques" valeur={periode.mode} onChange={(v) => maj({ mode: v as ModeStats })}
          options={[{ valeur: 'mois', libelle: 'Mois' }, { valeur: 'trimestre', libelle: 'Trimestre' }, { valeur: 'annee', libelle: 'Année' }, { valeur: 'plage', libelle: 'Dates' }]} />
      </div>
      {periode.mode === 'mois' && (
        <div><label style={lbl} htmlFor="st-mois">Mois</label>
          <input id="st-mois" type="month" value={periode.mois} max={aujourdhui.slice(0, 7)} onChange={(e) => maj({ mois: e.target.value })} style={{ ...inputStyle, maxWidth: 190 }} /></div>
      )}
      {periode.mode === 'trimestre' && (<>
        <div><label style={lbl} htmlFor="st-tri">Trimestre</label>
          <Selecteur id="st-tri" value={periode.trimestre.slice(5)} onChange={(v) => maj({ trimestre: `${periode.trimestre.slice(0, 4)}-${v}` })} style={{ ...inputStyle, maxWidth: 110 }}>
            {TRIMESTRES.map((t) => <option key={t} value={t}>{t}</option>)}</Selecteur></div>
        <div><label style={lbl} htmlFor="st-tri-an">Année</label>
          <Selecteur id="st-tri-an" value={periode.trimestre.slice(0, 4)} onChange={(v) => maj({ trimestre: `${v}-${periode.trimestre.slice(5)}` })} style={{ ...inputStyle, maxWidth: 120 }}>
            {annees.map((a) => <option key={a} value={a}>{a}</option>)}</Selecteur></div>
      </>)}
      {periode.mode === 'annee' && (
        <div><label style={lbl} htmlFor="st-an">Année</label>
          <Selecteur id="st-an" value={periode.annee} onChange={(v) => maj({ annee: v })} style={{ ...inputStyle, maxWidth: 120 }}>
            {annees.map((a) => <option key={a} value={a}>{a}</option>)}</Selecteur></div>
      )}
      {periode.mode === 'plage' && (<>
        <div><label style={lbl} htmlFor="st-du">Du</label>
          <input id="st-du" type="date" value={periode.du} max={periode.au || undefined} onChange={(e) => maj({ du: e.target.value })} style={{ ...inputStyle, maxWidth: 170 }} /></div>
        <div><label style={lbl} htmlFor="st-au">Au (inclus)</label>
          <input id="st-au" type="date" value={periode.au} min={periode.du || undefined} onChange={(e) => maj({ au: e.target.value })} style={{ ...inputStyle, maxWidth: 170 }} /></div>
      </>)}
    </div>
  );
}

export function StatistiquesNC() {
  const { ncs, booted, loading } = useGed();
  const aujourdhui = aujourdhuiIso();
  const [periode, setPeriode] = useState<PeriodeStats>(() => periodeParDefaut(aujourdhui));
  const [contact, poserContact] = useState<string>(() => (typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('contact') || ''));
  const [corr, setCorr] = useState<{ lignes: Ligne[]; peutReleve: boolean } | null>(null);
  const [erreurCorr, setErreurCorr] = useState<string | null>(null);
  const [dim, setDim] = useState<Dim>('nature');

  /* Le contact choisi vit dans l'adresse : un lien vers ses statistiques se partage. */
  const setContact = (c: string) => {
    poserContact(c);
    const url = new URL(window.location.href);
    if (c) url.searchParams.set('contact', c); else url.searchParams.delete('contact');
    window.history.replaceState(window.history.state, '', url);
  };

  /* Les correspondants à qui des NC ou des réclamations sont rattachées (fiches de l'annuaire), avec leurs adresses. */
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/correspondants', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: { lignes: Ligne[]; peutReleve: boolean }) => { if (vivant) setCorr({ lignes: j.lignes, peutReleve: j.peutReleve }); })
      .catch((e: Error) => { if (vivant) setErreurCorr(e.message); });
    return () => { vivant = false; };
  }, []);

  const nomsCorr = useNomsContacts(ncs.map((n) => n.correspondantCode || '').filter(Boolean));
  const bornes = bornesStats(periode);
  const prec = periodePrecedenteStats(periode);
  const dans = useMemo(() => (bornes ? ncsEntre(ncs, bornes.du, bornes.au) : []), [ncs, bornes]);
  const avant = useMemo(() => (prec ? ncsEntre(ncs, prec.du, prec.au) : []), [ncs, prec]);
  const evolution = useMemo(() => moisEvolution(periode).map((m) => {
    const b = bornesStats({ ...periode, mode: 'mois', mois: m });
    return { mois: m, n: b ? ncsEntre(ncs, b.du, b.au).length : 0 };
  }), [ncs, periode]);
  const dist = useMemo(() => {
    const m: Record<string, number> = {};
    for (const n of dans) {
      const k = dim === 'nature' ? (natureMeta(natureDe(n))?.label || '— à préciser')
        : dim === 'releve' ? (NC_TYPES.find((t) => t.key === (NC_CANAUX.find((c) => c.key === n.canal)?.releve || n.ncType))?.label || '— non précisé')
        : dim === 'equipement' ? (n.equipementCode || '— sans appareil')
        : dim === 'correspondant' ? (n.correspondantCode ? nomsCorr.get(n.correspondantCode)?.nom || n.correspondantCode : '— sans correspondant')
        : dim === 'source' ? (NC_SOURCES.find((s) => s.key === n.source)?.label || n.source)
        : dim === 'canal' ? (libelleCanal(n.canal) || '— non précisé')
        : dim === 'gravite' ? (NC_GRAVITES.find((g) => g.key === n.gravite)?.label || n.gravite)
        : dim === 'statut' ? (NC_STATES[n.statut]?.label || n.statut)
        : (n.processus || '—');
      m[k] = (m[k] || 0) + 1;
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [dans, dim, nomsCorr]);
  const parCorrespondant = useMemo(() => {
    const m = new Map<string, { code: string; n: number; graves: number }>();
    for (const n of dans) {
      const c = n.correspondantCode; if (!c) continue;
      const e = m.get(c) || { code: c, n: 0, graves: 0 };
      e.n += 1; if (['majeure', 'critique'].includes(String(n.gravite))) e.graves += 1;
      m.set(c, e);
    }
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [dans]);
  const rec = useMemo(() => {
    const an = ncDepuis(ncs, aujourdhui);
    return { problemes: recurrences(an, 'probleme'), appareils: recurrences(an, 'equipementCode'), correspondants: recurrences(an, 'correspondantCode') };
  }, [ncs, aujourdhui]);

  const ligneContact = corr?.lignes.find((l) => l.code === contact) || null;
  const graves = dans.filter((n) => ['majeure', 'critique'].includes(String(n.gravite))).length;
  const cloturees = dans.filter((n) => n.statut === 'cloturee').length;
  const maxEvo = Math.max(1, ...evolution.map((e) => e.n));
  const maxDist = Math.max(1, ...dist.map(([, v]) => v));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...card, padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <BarrePeriode periode={periode} onChange={setPeriode} aujourdhui={aujourdhui} />
        <span style={{ flex: 1 }} />
        <div style={{ minWidth: 260 }}>
          <label style={lbl} htmlFor="st-perimetre">Périmètre</label>
          <Selecteur id="st-perimetre" value={contact} onChange={setContact} style={inputStyle}>
            <option value="">Tout le laboratoire</option>
            {(corr?.lignes || []).map((l) => <option key={l.code} value={l.code}>{l.nom}{l.compte.ncs ? ` (${l.compte.ncs} NC)` : ''}</option>)}
          </Selecteur>
        </div>
      </div>

      {contact ? (
        !corr && !erreurCorr ? <Skeleton rows={3} />
          : ligneContact ? <PanneauReleve key={ligneContact.code} ligne={ligneContact} periode={periode} onFerme={() => setContact('')} />
            : <EmptyState titre="Aucune non-conformité ni réclamation n’est rattachée à ce contact." indice={erreurCorr ? `Lecture impossible : ${erreurCorr}` : 'Choisissez un autre correspondant, ou « Tout le laboratoire ».'} />
      ) : enChargement(booted, loading, ncs.length) ? (
        /* Le registre n'est pas encore là : « aucune NC sur la période » serait une affirmation fausse. */
        <Skeleton rows={4} />
      ) : !bornes ? (
        <div style={{ ...card, padding: 14, fontSize: 13 }}>La période est incomplète ou inversée.</div>
      ) : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {tuile(dans.length, `non-conformité(s) retenue(s) — ${bornes.libelle}`)}
          {tuile(ecartEnClair(dans.length, avant.length), `par rapport à ${prec?.libelle || 'la période précédente'} (${avant.length})`)}
          {tuile(graves, 'majeure(s) ou critique(s)')}
          {tuile(dans.length - cloturees, 'en cours de traitement')}
          {tuile(cloturees, 'clôturée(s)')}
        </div>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>NC retenues : ni au triage, ni annulées — comptées au jour de leur constat. Les mêmes règles que le document envoyé à un correspondant.</p>

        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ ...card, padding: 16 }}>
            {titre('Évolution mois par mois')}
            {evolution.map((e) => <Barre key={e.mois} libelle={moisEnClair(e.mois)} n={e.n} max={maxEvo} />)}
          </div>
          <div style={{ ...card, padding: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {titre('Répartition')}
              <Selecteur id="st-dim" aria-label="Répartir par" value={dim} onChange={(v) => setDim(v as Dim)} style={{ ...inputStyle, width: 'auto', minWidth: 170 }}>
                {DIMS.map((d) => <option key={d.valeur} value={d.valeur}>{d.libelle}</option>)}
              </Selecteur>
            </div>
            {dist.length === 0 ? <EmptyState titre="Aucune non-conformité sur la période." indice="Ce n’est pas une erreur : c’est un résultat." />
              : dist.map(([k, v]) => <Barre key={k} libelle={k} n={v} max={maxDist} />)}
          </div>
        </div>

        <div style={{ ...card, padding: 16, display: 'grid', gap: 8 }}>
          {titre('Par correspondant')}
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Ouvrez un correspondant pour lire ses statistiques et, si vous êtes coordinateur ou responsable qualité, les lui envoyer.</p>
          <Tableau id="nc-statistiques-correspondants" niveau="simple" ajusterLargeur lignes={parCorrespondant} cleLigne={(x) => x.code}
            vide={{ titre: 'Aucune non-conformité imputée à un correspondant sur la période.' }}
            ligneProps={(x) => ligneOuvrable(() => setContact(x.code), `Statistiques de ${nomsCorr.get(x.code)?.nom || x.code}`)}
            colonnes={[
              { cle: 'nom', libelle: 'Correspondant', valeur: (x) => nomsCorr.get(x.code)?.nom || x.code, rendu: (x) => <strong style={{ color: 'var(--text-strong)' }}>{nomsCorr.get(x.code)?.nom || x.code}</strong> },
              { cle: 'n', libelle: 'NC', type: 'nombre', valeur: (x) => x.n, rendu: (x) => x.n },
              { cle: 'graves', libelle: 'Majeures ou critiques', type: 'nombre', valeur: (x) => x.graves, rendu: (x) => (x.graves ? <Pastille ton="warning" compacte>{x.graves}</Pastille> : '0') },
              { cle: 'envoi', libelle: 'Envoi', valeur: (x) => (corr?.lignes.find((l) => l.code === x.code)?.destinatairesQualite.adresses.length ? 'courriel' : 'pdf'),
                rendu: (x) => { const l = corr?.lignes.find((y) => y.code === x.code); return !l ? '—' : l.destinatairesQualite.adresses.length ? <Pastille ton="success" compacte>courriel</Pastille> : <Pastille ton="neutral" compacte>PDF à remettre</Pastille>; } },
            ]} />
        </div>

        <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
          {titre('Ce qui se répète — 12 derniers mois')}
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
            Un même problème, un même appareil ou un même correspondant revenu au moins deux fois : l’action corrective n’a peut-être pas porté.
          </p>
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            {([['Problèmes', rec.problemes], ['Appareils', rec.appareils], ['Correspondants', rec.correspondants]] as const).map(([t, liste]) => (
              <div key={t}>
                <div style={{ ...lbl, marginBottom: 4 }}>{t}</div>
                {liste.length === 0
                  ? <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune récurrence.</span>
                  : liste.slice(0, 8).map((r) => (
                    <div key={r.valeur} style={{ fontSize: 12.5, lineHeight: 1.6 }} title={r.exemple}>
                      <strong style={t === 'Correspondants' ? undefined : { fontFamily: 'var(--font-mono)' }}>{t === 'Correspondants' ? nomsCorr.get(r.valeur)?.nom || r.valeur : r.valeur}</strong> × {r.n}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}
